import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  Modal,
  Row,
  Space,
  Spin,
  Statistic,
  Table,
  Tag,
  Typography,
} from "antd";
import {
  AlertOutlined,
  BarChartOutlined,
  DropboxOutlined,
  LinkOutlined,
  SwapOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";

import { apiClient } from "../../services/api/apiClient";
import { fmtKg } from "./adminDashboardShared";

dayjs.extend(isoWeek);

const { Text } = Typography;

const COLOR_DECLARED = "#1890ff";
const COLOR_AUDITED = "#52c41a";

interface ProdPayload {
  role: "admin";
  scope: "production";
  season: { id: number; label: string; year: number } | null;
  available_seasons: Array<{ id: number; label: string; year: number }>;
  kpis: {
    total_declared_kg: number;
    total_audited_kg: number;
    delta_pct: number | null;
    delta_kg: number;
    mean_decrease_pct: number;
    critical_decrease_farms: number;
    critical_threshold_pct: number;
  };
  by_province: Array<{
    province_name: string;
    declared_kg: number;
    audited_kg: number;
  }>;
  weekly_audited: Array<{
    week: string;
    audited_cum: number;
    declared_cum: number;
  }>;
  by_producer: Array<{
    producer_id: number;
    producer_name: string;
    province_name: string;
    farms_count: number;
    declared_kg: number;
    audited_kg: number;
    delta_pct: number | null;
    decrease_pct: number;
  }>;
}

interface Props {
  season: number | null;
}

interface BreakdownPayload {
  role: "admin";
  scope: "producer_breakdown";
  season: { id: number; label: string; year: number } | null;
  producer: { id: number; name: string | null } | null;
  audits: Array<{
    audit_id: number;
    reference: string;
    farm_name: string;
    start_date: string | null;
    finalized_at: string | null;
    status: string;
    audit_type: string;
    declared_kg: number;
    audited_kg: number;
    delta_pct: number | null;
    decrease_pct: number | null;
  }>;
}

export const AdminProductionPanel = ({ season }: Props) => {
  const [payload, setPayload] = useState<ProdPayload | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Estado del modal "Ver desglose" (opción B del plan de UX).
  const [breakdownProducer, setBreakdownProducer] = useState<{
    id: number;
    name: string;
  } | null>(null);
  const [breakdown, setBreakdown] = useState<BreakdownPayload | null>(null);
  const [breakdownLoading, setBreakdownLoading] = useState(false);

  useEffect(() => {
    if (season == null) return;
    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await apiClient.get<ProdPayload>(
          `/v1/dashboard/?scope=production&season=${season}`,
        );
        setPayload(res.data);
      } catch (err: any) {
        setError(
          err?.response?.data?.detail ||
            err?.message ||
            "No se pudo cargar el panel de producción",
        );
        setPayload(null);
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [season]);

  const provinceData = useMemo(
    () =>
      (payload?.by_province ?? []).slice(0, 15).map((r) => ({
        name: r.province_name,
        Declarada: Math.round(r.declared_kg),
        Auditada: Math.round(r.audited_kg),
      })),
    [payload?.by_province],
  );

  const weeklyData = useMemo(
    () =>
      (payload?.weekly_audited ?? []).map((p) => ({
        week: `W${dayjs(p.week).isoWeek()}`,
        "Kg auditados (acum.)": Math.round(p.audited_cum),
        "Kg declarados (acum.)": Math.round(p.declared_cum),
      })),
    [payload?.weekly_audited],
  );

  // Fetch del desglose cuando el usuario abre el modal: una fila
  // por auditoría del productor en la campaña actual, con declarado
  // vs auditado + delta por fila.
  useEffect(() => {
    if (!breakdownProducer || season == null) return;
    const run = async () => {
      setBreakdownLoading(true);
      try {
        const res = await apiClient.get<BreakdownPayload>(
          `/v1/dashboard/?scope=producer_breakdown&season=${season}&producer=${breakdownProducer.id}`,
        );
        setBreakdown(res.data);
      } catch (err: any) {
        // En caso de error, dejamos el modal con el estado vacío
        // (la propia tabla mostrará "sin datos").
        setBreakdown(null);
      } finally {
        setBreakdownLoading(false);
      }
    };
    run();
  }, [breakdownProducer, season]);

  // Opción A — abrir el listado de audits filtrado por este
  // productor + campaña (nueva pestaña, no se pierde el dashboard).
  // Filtros Refine en URL (`syncWithLocation`): usamos `q` que mapea
  // al search global del backend (producer_name/farm_name) y
  // `season_year` para acotar a la campaña actual.
  const openAuditsList = (producerName: string) => {
    if (!payload?.season) return;
    const params = new URLSearchParams();
    params.append("filters[0][field]", "q");
    params.append("filters[0][operator]", "contains");
    params.append("filters[0][value]", producerName);
    params.append("filters[1][field]", "season_year");
    params.append("filters[1][operator]", "eq");
    params.append("filters[1][value]", String(payload.season.year));
    window.open(`/audits?${params.toString()}`, "_blank");
  };

  // Dropdown-filter "Provincia" en el ranking de productores: una
  // entrada por provincia distinta.
  const producerProvinceFilters = useMemo(() => {
    const seen = new Set<string>();
    const out: Array<{ text: string; value: string }> = [];
    for (const row of payload?.by_producer ?? []) {
      const p = row.province_name || "—";
      if (!seen.has(p)) {
        seen.add(p);
        out.push({ text: p, value: p });
      }
    }
    return out.sort((a, b) => a.text.localeCompare(b.text));
  }, [payload?.by_producer]);

  if (error) return <Alert type="error" message={error} showIcon />;

  return (
    <Spin spinning={loading} tip="Cargando producción…">
      {payload && (
        <>
          {/* ── Fila 1 — KPIs ─────────────────────────────────── */}
          <Row gutter={[16, 16]}>
            <Col xs={12} md={6}>
              <Card style={{ height: "100%" }}>
                <Statistic
                  title="Producción declarada"
                  value={payload.kpis.total_declared_kg}
                  precision={0}
                  prefix={<DropboxOutlined />}
                  suffix="kg"
                  valueStyle={{ color: COLOR_DECLARED }}
                />
                <Text type="secondary" style={{ fontSize: 11 }}>
                  Comunicada por el productor
                </Text>
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card style={{ height: "100%" }}>
                <Statistic
                  title="Producción auditada"
                  value={payload.kpis.total_audited_kg}
                  precision={0}
                  prefix={<DropboxOutlined />}
                  suffix="kg"
                  valueStyle={{ color: COLOR_AUDITED }}
                />
                <Text type="secondary" style={{ fontSize: 11 }}>
                  Estimada por el auditor (Σ aforo bloques)
                </Text>
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card style={{ height: "100%" }}>
                <Statistic
                  title="Delta auditor vs productor"
                  value={payload.kpis.delta_pct ?? 0}
                  precision={1}
                  suffix="%"
                  prefix={<SwapOutlined />}
                  valueStyle={{
                    color:
                      (payload.kpis.delta_pct ?? 0) >= 0
                        ? COLOR_AUDITED
                        : "#f5222d",
                  }}
                />
                <Text type="secondary" style={{ fontSize: 11 }}>
                  Δ {fmtKg(Math.abs(payload.kpis.delta_kg))}
                </Text>
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card style={{ height: "100%" }}>
                <Statistic
                  title="Fincas con merma crítica"
                  value={payload.kpis.critical_decrease_farms}
                  prefix={<WarningOutlined />}
                  valueStyle={{ color: "#faad14" }}
                />
                <Text type="secondary" style={{ fontSize: 11 }}>
                  ≥ {payload.kpis.critical_threshold_pct}% merma declarada
                </Text>
                <div style={{ marginTop: 6 }}>
                  <Tag icon={<AlertOutlined />} color="orange">
                    Media ponderada:{" "}
                    {payload.kpis.mean_decrease_pct.toFixed(1)}%
                  </Tag>
                </div>
              </Card>
            </Col>
          </Row>

          {/* ── Fila 2 — Por provincia ────────────────────────── */}
          <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
            <Col xs={24}>
              <Card title="Producción por provincia · declarada vs auditada">
                <ResponsiveContainer width="100%" height={360}>
                  <BarChart
                    data={provinceData}
                    margin={{ left: 10, right: 20, top: 10 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis
                      dataKey="name"
                      tick={{ fontSize: 11 }}
                      interval={0}
                      angle={-20}
                      textAnchor="end"
                      height={70}
                    />
                    <YAxis
                      tick={{ fontSize: 11 }}
                      tickFormatter={(v: number) =>
                        v >= 1000000
                          ? `${(v / 1000000).toFixed(1)}M`
                          : v >= 1000
                            ? `${(v / 1000).toFixed(0)}k`
                            : String(v)
                      }
                    />
                    <Tooltip formatter={(v: any) => fmtKg(Number(v))} />
                    <Legend />
                    <Bar dataKey="Declarada" fill={COLOR_DECLARED} />
                    <Bar dataKey="Auditada" fill={COLOR_AUDITED} />
                  </BarChart>
                </ResponsiveContainer>
              </Card>
            </Col>
          </Row>

          {/* ── Fila 3 — Progresión semanal ───────────────────── */}
          <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
            <Col xs={24}>
              <Card title="Kg acumulados por semana · declarados vs auditados">
                <ResponsiveContainer width="100%" height={280}>
                  <LineChart data={weeklyData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis
                      dataKey="week"
                      tick={{ fontSize: 11 }}
                      interval={0}
                      height={30}
                    />
                    <YAxis
                      tick={{ fontSize: 11 }}
                      tickFormatter={(v: number) =>
                        v >= 1000000
                          ? `${(v / 1000000).toFixed(1)}M`
                          : v >= 1000
                            ? `${(v / 1000).toFixed(0)}k`
                            : String(v)
                      }
                    />
                    <Tooltip formatter={(v: any) => fmtKg(Number(v))} />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="Kg declarados (acum.)"
                      stroke={COLOR_DECLARED}
                      strokeWidth={2}
                      dot={false}
                    />
                    <Line
                      type="monotone"
                      dataKey="Kg auditados (acum.)"
                      stroke={COLOR_AUDITED}
                      strokeWidth={2}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Card>
            </Col>
          </Row>

          {/* ── Fila 4 — Top productores ──────────────────────── */}
          <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
            <Col xs={24}>
              <Card title="Top 25 productores por kg declarados">
                <Table
                  size="small"
                  rowKey="producer_id"
                  dataSource={payload.by_producer}
                  pagination={{ pageSize: 10, showSizeChanger: false }}
                  scroll={{ x: 1000 }}
                  columns={[
                    {
                      title: "Productor",
                      dataIndex: "producer_name",
                      key: "producer_name",
                      sorter: (a, b) =>
                        a.producer_name.localeCompare(b.producer_name),
                    },
                    {
                      title: "Provincia",
                      dataIndex: "province_name",
                      key: "province_name",
                      sorter: (a, b) =>
                        a.province_name.localeCompare(b.province_name),
                      filters: producerProvinceFilters,
                      filterSearch: true,
                      onFilter: (value, record) =>
                        (record.province_name || "—") === value,
                    },
                    {
                      title: "Fincas",
                      dataIndex: "farms_count",
                      key: "farms_count",
                      width: 80,
                      align: "right",
                      sorter: (a, b) => a.farms_count - b.farms_count,
                    },
                    {
                      title: "Kg declarados",
                      dataIndex: "declared_kg",
                      key: "declared_kg",
                      width: 150,
                      align: "right",
                      sorter: (a, b) => a.declared_kg - b.declared_kg,
                      defaultSortOrder: "descend",
                      render: (v: number) => fmtKg(v),
                    },
                    {
                      title: "Kg auditados",
                      dataIndex: "audited_kg",
                      key: "audited_kg",
                      width: 150,
                      align: "right",
                      sorter: (a, b) => a.audited_kg - b.audited_kg,
                      render: (v: number) => fmtKg(v),
                    },
                    {
                      title: "Delta %",
                      dataIndex: "delta_pct",
                      key: "delta_pct",
                      width: 100,
                      align: "right",
                      sorter: (a, b) =>
                        (a.delta_pct ?? 0) - (b.delta_pct ?? 0),
                      render: (v: number | null) => {
                        if (v == null) return "—";
                        const color =
                          Math.abs(v) < 10 ? "default" : v > 0 ? "green" : "red";
                        return (
                          <Tag color={color}>
                            {v > 0 ? "+" : ""}
                            {v.toFixed(1)}%
                          </Tag>
                        );
                      },
                    },
                    {
                      title: "Acciones",
                      key: "actions",
                      width: 110,
                      fixed: "right",
                      render: (_: any, r) => (
                        <Space size="small">
                          <Button
                            size="small"
                            icon={<BarChartOutlined />}
                            title="Ver desglose por auditoría (declarado vs auditado)"
                            onClick={() =>
                              setBreakdownProducer({
                                id: r.producer_id,
                                name: r.producer_name,
                              })
                            }
                          />
                          <Button
                            size="small"
                            icon={<LinkOutlined />}
                            title="Abrir listado de auditorías del productor en nueva pestaña"
                            onClick={() => openAuditsList(r.producer_name)}
                          />
                        </Space>
                      ),
                    },
                  ]}
                />
              </Card>
            </Col>
          </Row>
        </>
      )}

      {/* Modal "Ver desglose" — fila por auditoría del productor
          con declarado vs auditado + delta. Caso de uso: detectar
          en qué audit concreto difiere más el aforo del auditor
          respecto a la cifra declarada por el productor. */}
      <Modal
        open={!!breakdownProducer}
        onCancel={() => {
          setBreakdownProducer(null);
          setBreakdown(null);
        }}
        footer={null}
        width={960}
        title={
          breakdownProducer
            ? `Desglose por auditoría · ${breakdownProducer.name}`
            : "Desglose por auditoría"
        }
      >
        <Spin spinning={breakdownLoading}>
          <Table
            size="small"
            rowKey="audit_id"
            dataSource={breakdown?.audits ?? []}
            pagination={{ pageSize: 10, showSizeChanger: false }}
            scroll={{ x: 820 }}
            columns={[
              {
                title: "Finca",
                dataIndex: "farm_name",
                key: "farm_name",
                sorter: (a, b) => a.farm_name.localeCompare(b.farm_name),
              },
              {
                title: "Ref.",
                dataIndex: "reference",
                key: "reference",
                width: 110,
                render: (ref: string, r) => (
                  <a
                    href={`/audits/${r.audit_id}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {ref}
                  </a>
                ),
              },
              {
                title: "Tipo",
                dataIndex: "audit_type",
                key: "audit_type",
                width: 100,
              },
              {
                title: "Estado",
                dataIndex: "status",
                key: "status",
                width: 110,
                render: (v: string) => {
                  const map: Record<string, { color: string; label: string }> = {
                    planned: { color: "blue", label: "Planificada" },
                    performed: { color: "purple", label: "Ejecutada" },
                    submitted: { color: "gold", label: "Pdte. revisión" },
                    finalized: { color: "green", label: "Finalizada" },
                    canceled: { color: "red", label: "Cancelada" },
                  };
                  const m = map[v] ?? { color: "default", label: v };
                  return <Tag color={m.color}>{m.label}</Tag>;
                },
              },
              {
                title: "Fecha fin.",
                dataIndex: "finalized_at",
                key: "finalized_at",
                width: 110,
                render: (v: string | null) =>
                  v ? dayjs(v).format("DD/MM/YYYY") : "—",
              },
              {
                title: "Kg declarados",
                dataIndex: "declared_kg",
                key: "declared_kg",
                width: 140,
                align: "right",
                sorter: (a, b) => a.declared_kg - b.declared_kg,
                render: (v: number) => fmtKg(v),
              },
              {
                title: "Kg auditados",
                dataIndex: "audited_kg",
                key: "audited_kg",
                width: 140,
                align: "right",
                sorter: (a, b) => a.audited_kg - b.audited_kg,
                render: (v: number) => fmtKg(v),
              },
              {
                title: "Δ%",
                dataIndex: "delta_pct",
                key: "delta_pct",
                width: 90,
                align: "right",
                sorter: (a, b) =>
                  (a.delta_pct ?? 0) - (b.delta_pct ?? 0),
                render: (v: number | null) => {
                  if (v == null) return "—";
                  const color =
                    Math.abs(v) < 10 ? "default" : v > 0 ? "green" : "red";
                  return (
                    <Tag color={color}>
                      {v > 0 ? "+" : ""}
                      {v.toFixed(1)}%
                    </Tag>
                  );
                },
              },
              {
                title: "Merma %",
                dataIndex: "decrease_pct",
                key: "decrease_pct",
                width: 90,
                align: "right",
                render: (v: number | null) =>
                  v != null ? `${v.toFixed(1)}%` : "—",
              },
            ]}
          />
        </Spin>
      </Modal>
    </Spin>
  );
};
